import DOMPurify from 'dompurify';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleBody from '../../components/noticia/ArticleBody';
import OtherNews from '../../components/noticia/OtherNews';
import ShareLinks from '../../components/noticia/ShareLinks';
import Breadcrumb from '../../components/shared/Breadcrumb';
import Button from '../../components/shared/Button';
import Container from '../../components/shared/Container';
import { getNoticiaById, noticias, type Block, type NoticiaPublica } from '../../data/noticias';
import { useAsyncData } from '../../hooks/useAsyncData';
import { getNewsById, getPublishedNews, type News } from '../../services/newsService';
import styles from './NoticiaPage.module.css';

/**
 * Texto plano de un fragmento de HTML.
 *
 * `DOMParser` en vez de `innerHTML` sobre un `<div>`: aunque ese `<div>` nunca
 * se cuelga del documento, el navegador igualmente puede llegar a pedir sus
 * recursos (`<img src>`) y disparar sus manejadores inline (`onerror`, etc.) —
 * es el vector de XSS "innerHTML en nodo desconectado", que parece inofensivo
 * y no lo es. Un documento de `DOMParser` no ejecuta scripts ni carga nada.
 */
function extractPlainText(html: string): string {
  const documento = new DOMParser().parseFromString(html, 'text/html');
  return documento.body.textContent ?? '';
}

function isNews(item: News | (typeof noticias)[number]): item is News {
  return 'content' in item && 'imageData' in item;
}

const BackToNoticias = () => (
  <Link className={styles.backLink} to="/noticias">
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
    Volver a Noticias
  </Link>
);

export default function NoticiaPage() {
  const { id } = useParams<{ id: string }>();

  // El servicio puede estar llamando al API, asi que la noticia llega despues
  // del primer render.
  const cargarNoticia = useCallback(
    (signal: AbortSignal) => (id ? getNewsById(id, signal) : Promise.resolve(undefined)),
    [id],
  );
  const { datos: noticiaDelServicio, cargando } = useAsyncData<News | undefined>(
    cargarNoticia,
    undefined,
  );

  const cargarPublicadas = useCallback((signal: AbortSignal) => getPublishedNews(signal), []);
  const { datos: publicadas } = useAsyncData<News[]>(cargarPublicadas, []);

  // Las estaticas viven en el codigo: se resuelven sin esperar a nadie.
  const idEstatico = Number.parseInt(id ?? '', 10);
  const estatica = Number.isNaN(idEstatico) ? undefined : getNoticiaById(idEstatico);

  const noticia: News | (typeof noticias)[number] | undefined = noticiaDelServicio ?? estatica;

  if (!noticia) {
    // Mientras se espera al servicio no se puede afirmar que no exista.
    if (cargando) return null;

    return (
      <Container className={styles.notFound}>
        <h1 className={styles.notFoundTitle}>Noticia no encontrada</h1>
        <p className={styles.notFoundText}>La noticia que buscas no existe o ya no está disponible.</p>
        <Button href="/noticias">Volver a Noticias</Button>
      </Container>
    );
  }

  // Para noticias del newsService, usar el HTML completo
  // Para noticias estáticas, usar el sistema de blocks
  let formattedNoticia: NoticiaPublica;
  let fullHtmlContent: string | null = null;

  if (isNews(noticia)) {
    const plainText = extractPlainText(noticia.content);
    const excerpt = plainText.substring(0, 150).trim() + (plainText.length > 150 ? '...' : '');
    
    formattedNoticia = {
      id: noticia.id,
      title: noticia.title,
      excerpt,
      image: noticia.imageData,
      date: new Date(noticia.publishedAt || noticia.createdAt).toLocaleDateString('es-ES'),
      blocks: undefined,
      url: undefined,
    };
    fullHtmlContent = noticia.content; // Guardar HTML completo
  } else {
    formattedNoticia = noticia;
  }

  const blocks: Block[] = formattedNoticia.blocks ?? [{ kind: 'p', text: formattedNoticia.excerpt }];
  const allNoticias: NoticiaPublica[] = [...noticias, ...publicadas.map((n) => {
    const plainText = extractPlainText(n.content);
    const excerpt = plainText.substring(0, 150).trim() + (plainText.length > 150 ? '...' : '');
    
    return {
      id: n.id,
      title: n.title,
      excerpt,
      image: n.imageData,
      date: new Date(n.publishedAt || n.createdAt).toLocaleDateString('es-ES'),
    };
  })];
  const others = allNoticias.filter((item) => item.id !== formattedNoticia.id).slice(0, 3);

  return (
    <>
      <article className={styles.article}>
        <section className={styles.header}>
          <div className={styles.topRow}>
            <Breadcrumb
              tone="light"
              items={[{ label: 'Inicio', to: '/' }, { label: 'Noticias', to: '/noticias' }, { label: 'Noticia' }]}
            />
            <BackToNoticias />
          </div>

          <div className={styles.date}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            {formattedNoticia.date}
          </div>
          <h1 className={styles.title}>{formattedNoticia.title}</h1>
        </section>

        {formattedNoticia.image ? (
          <section className={styles.mediaSection}>
            {/* Sin `src` no se pinta la etiqueta: un src vacío hace que el
                navegador vuelva a pedir la página entera. El API admite
                noticias sin imagen (`image: null` en el contrato). */}
            <img className={styles.heroImage} src={formattedNoticia.image} alt={formattedNoticia.title} />
          </section>
        ) : null}

        <section className={styles.bodySection}>
          {fullHtmlContent ? (
            // Para noticias del newsService, renderizar HTML completo.
            // Saneado con DOMPurify: el contenido llega del backend/CMS, y
            // aunque hoy no hay ningún formulario en esta web que lo escriba,
            // inyectarlo sin sanear sería un vector directo de XSS si esa
            // fuente llegara a devolver algo malicioso.
            <div
              className={styles.body}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fullHtmlContent) }}
            />
          ) : (
            // Para noticias estáticas, usar ArticleBody con blocks
            <ArticleBody blocks={blocks} />
          )}
        </section>

        <section className={styles.shareSection}>
          <ShareLinks />
        </section>
      </article>

      <OtherNews noticias={others} />
    </>
  );
}
